import { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
    Button,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageHeader } from '../components/PageHeader';
import { paymentsApi } from '../services/payments-api.service';

interface CenterWithTeachers {
    centerId: string;
    slug: string;
    name: {
        en: string;
        fr: string;
    };
    state: string;
    teachers: [string, string, string][]; // [fullName, forename, surname]
    marketingRegions: string[];
}

type ViewMode = 'centres' | 'teachers';

export function CentresPage() {
    const [viewMode, setViewMode] = useState<ViewMode>('centres');
    const [centers, setCenters] = useState<CenterWithTeachers[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const handleRefresh = () => {
        setRefreshTrigger((prev) => prev + 1);
    };

    useEffect(() => {
        const fetchCenters = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await paymentsApi.getCentersWithTeachers();
                if (result.success) {
                    setCenters(result.data);
                } else {
                    setError('Failed to fetch centers data');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to fetch centers');
            } finally {
                setLoading(false);
            }
        };

        fetchCenters();
    }, [refreshTrigger]);

    const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    };

    // Check for centers with missing state codes
    const centersWithMissingState = () => {
        return centers.filter((center) => !center.state || center.state.trim() === '');
    };

    // Group teachers by name for centers-by-teacher view
    const teacherToCenters = () => {
        const teacherMap = new Map<string, CenterWithTeachers[]>();

        centers.forEach((center) => {
            center.teachers.forEach(([fullName]) => {
                if (!teacherMap.has(fullName)) {
                    teacherMap.set(fullName, []);
                }
                teacherMap.get(fullName)!.push(center);
            });
        });

        return Array.from(teacherMap.entries())
            .filter(([, centersList]) => {
                // Exclude teachers who only have centers with slug 'test'
                return centersList.some((center) => center.slug !== 'test');
            })
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([teacherName, centersList]) => ({
                teacherName,
                centers: centersList,
            }));
    };

    const renderCentres = () => (
        <TableContainer component={Paper} sx={{ width: 'fit-content' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Centre</TableCell>
                        <TableCell>Slug</TableCell>
                        <TableCell>Marketing Regions</TableCell>
                        <TableCell>Teachers</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {centers
                        .sort((a, b) => a.name.en.localeCompare(b.name.en))
                        .map((center) => (
                            <TableRow key={center.centerId} hover>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                        {center.name.en}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{center.slug}</Typography>
                                </TableCell>
                                <TableCell>
                                    {center.marketingRegions.length > 0 ? (
                                        <Typography variant="body2">
                                            {center.marketingRegions.sort().join(' | ')}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2">--</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {center.teachers.length > 0 ? (
                                        <Typography variant="body2">
                                            {center.teachers
                                                .map(([fullName]) => fullName)
                                                .sort()
                                                .join(' | ')}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2">--</Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const renderTeachers = () => {
        const teacherData = teacherToCenters();

        return (
            <TableContainer component={Paper} sx={{ width: 'fit-content' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Teacher</TableCell>
                            <TableCell>Centres</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {teacherData.map(({ teacherName, centers: teacherCenters }) => (
                            <TableRow key={teacherName} hover>
                                <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                        {teacherName}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {teacherCenters
                                            .sort((a, b) => a.name.en.localeCompare(b.name.en))
                                            .map((center) => center.name.en)
                                            .join(' | ')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <PageHeader currentPath="/centres">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ fontSize: 28, mt: 0.3, mr: 1.5, color: 'primary.main' }} />
                    <Typography variant="h5" component="h1" fontWeight={600}>
                        Centres
                    </Typography>
                </Box>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading}>
                    Refresh
                </Button>
            </Box>

            {/* View Mode Toggle */}
            <Box sx={{ mb: 3 }}>
                <ToggleButtonGroup
                    color="primary"
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                >
                    <ToggleButton value="centres">Centres</ToggleButton>
                    <ToggleButton value="teachers">Teachers</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Missing State Code Alert */}
            {!loading && !error && centersWithMissingState().length > 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Warning: {centersWithMissingState().length} centre(s) are missing province codes:
                    </Typography>
                    <Typography variant="body2">
                        {centersWithMissingState()
                            .map((center) => center.name.en)
                            .sort()
                            .join(' | ')}
                    </Typography>
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Content */}
            {!loading && !error && (
                <>
                    {viewMode === 'centres' && renderCentres()}
                    {viewMode === 'teachers' && renderTeachers()}
                </>
            )}
        </PageHeader>
    );
}
